// Frontend questionnaire data - matches backend structure

export interface QuestionnaireQuestion {
  id: string;
  category: string;
  question: string;
  guidance?: string;
  initialReview?: string; // For CMA assessments - pre-filled Initial Review text
  required: boolean;
  type: 'yes_no' | 'text' | 'comment';
  allowNA?: boolean; // When true, this is the one question per section that may be answered N/A
}

export interface QuestionnaireSection {
  id: string;
  title: string;
  description?: string;
  questions: QuestionnaireQuestion[];
  minRequired: number;
}

// CMDA Control Design Assessment Questions
export const CMDA_CONTROL_QUESTIONS: QuestionnaireSection = {
  id: 'control_design',
  title: 'Control Design Assessment (CDA)',
  description: 'Control Design Rating: Adequate/Inadequate',
  minRequired: 17, // 17 out of 18 questions must be answered Yes for adequacy (C6 must be No)
  questions: [
    {
      id: 'C1',
      category: 'Regulations/Obligations related to Control',
      question: 'Is the Control a Key Control to mitigate the risk of non-adherence for each mapped obligation/Regulation?',
      guidance: 'If no, work with compliance to determine if Obligation(s) should be unmapped from the Risk/Control. If determined there are no Obligations required then select Yes and provide rationale in comments.',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C2',
      category: 'Regulations/Obligations related to Control',
      question: 'Does the Control cover both ongoing compliance with the Regulations/Obligation and when changes are made to the current environment?',
      guidance: 'If not, is there a related Control that covers what this Control doesn\'t care for? Cite Control ID in comments. If determined there are no Obligations required then select Yes and provide rationale in comments.',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C3',
      category: 'Control Scope (Process, Products, Segments, Channels)',
      question: 'Does the Control cover all applicable products within the line of business?',
      guidance: 'If not, does the Control describe what products are covered? Is there another Control that covers other applicable products? Cite Control ID in comments',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C4',
      category: 'Control Scope (Process, Products, Segments, Channels)',
      question: 'Does the Control cover all applicable customer segments?',
      guidance: 'If not, does the Control describe what customer segments are covered? Is there another related Control that covers all other segments? Cite Control ID in comments',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C5',
      category: 'Control Scope (Process, Products, Segments, Channels)',
      question: 'Does the Control cover all applicable channels?',
      guidance: 'If not, does the Control describe what channel is covered? Is there another related Control that covers all other channels? Cite Control ID in comments',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C6',
      category: 'Risk Mitigation',
      question: 'Are additional Controls needed to mitigate the Risk to an acceptable level?',
      guidance: 'If yes, identify the Control(s) that is/are missing and add Comments',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C7',
      category: 'Risk Mitigation',
      question: 'Are all applicable Products mapped in organization Risk & Controls?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C8',
      category: 'Risk Mitigation',
      question: 'Does the Control or Control(s) mitigate the risk within the Risk Statement to an acceptable level (within risk appetite)?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C9',
      category: 'Sustainability',
      question: 'Is the Control Sustainable given expected/forseeable changes to the process?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C10',
      category: 'Control Performer',
      question: 'Is the appropriate person at the appropriate Level performing the Control?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C11',
      category: 'Control Execution',
      question: 'Are there instructions for performing the Control?',
      guidance: 'If yes, are they current and consistent with the Control Description? Link or Location of instructions for performing the Control',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C12',
      category: 'Control Execution',
      question: 'Is there evidence of proper Control execution (in line with instructions) and retained?',
      guidance: 'In comments provide link or location of Evidence of Control Execution',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C13',
      category: 'Control Execution',
      question: 'Do instructions include actions to be taken when Control identifies an error or a problem?',
      guidance: 'If Yes, provide comments on actions.',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C14',
      category: 'Control Design Requirements',
      question: 'Does the control satisfy the minimum control design requirement?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C15',
      category: 'Control Design Requirements',
      question: 'Does the control description outline the intended operation of the control, including 1. what triggers the control 2. the action the control takes to mitigate the risk in a way that is clear, concise, jargon-free and understandable by a third party? 3. the scope subject and period under review of the control?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C16',
      category: 'Control Design Requirements',
      question: 'Is the control description complete and accurately reflect the current state including 5Ws?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C17',
      category: 'Control Design Requirements',
      question: 'Is the control correctly categorized against the control taxonomy?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'C18',
      category: 'Control Design Requirements',
      question: 'Does the control objective defines the intended outcome of the control?',
      required: true,
      type: 'yes_no'
    }
  ]
};

// CMDA Monitoring Design Assessment Questions
export const CMDA_MONITORING_QUESTIONS: QuestionnaireSection = {
  id: 'monitoring_design',
  title: 'Monitoring Design Assessment (MDA)',
  description: 'Monitoring Design Rating: Adequate/Inadequate',
  minRequired: 9,
  questions: [
    {
      id: 'M1',
      category: 'Monitoring Execution',
      question: 'Who: Is the Monitoring performed by the Right Person /Right Level?',
      guidance: 'Is the Monitoring performed by a knowledgeable person who is familiar with the Control. Is the reporting line clear from perceived conflict of interest? Is there clear segregation of duties? Is the person executing the Control independent from person executing Monitoring? Is there documentation in the monitoring procedures that clearly shows the segregation of duties?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M2',
      category: 'Monitoring Execution',
      question: 'When: Is the Monitoring performed at the Right Frequency / Right Stage?',
      guidance: 'Consider if the Monitoring frequency can identify emerging vulnerabilities and/or Control weaknesses quickly enough. Consider if the Monitoring is performed on a timely basis to provide an early warning of impending risk',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M3',
      category: 'Monitoring Execution',
      question: 'How: Is the Monitoring Sustainable?',
      guidance: 'Consider whether the Monitoring is designed to operate effectively if operating conditions change (e.g., If business volumes increase, regulatory changes in the pipeline). Consider whether the Monitoring has a key resource/person dependency. Consider if the data source is accurate. Consider if there are instructions/procedures/documents on how the monitoring is to be performed',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M4',
      category: 'Monitoring Execution',
      question: 'What: Does the Monitoring reflect whether or not Control is working as designed?',
      guidance: 'Consider if the Monitoring is the best method of evaluating the key Control, as opposed to the only method currently available. Consider whether there has been an assessment of Monitoring design following: impacted operational losses, significant issues, identified management observations, etc.',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M5',
      category: 'Monitoring Execution',
      question: 'Is there evidence of proper execution?',
      guidance: 'Consider if the Monitoring was properly executed. Evidence of proper escalation when performance does not meet threshold?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M6',
      category: 'Monitoring Execution',
      question: 'Is there evidence of proper escalation when performance does not meet threshold?',
      guidance: 'Assess if person performing the Monitoring can articulate proper escalation process should he/she identify the Control is performing outside of acceptable threshold. Consider if Monitoring procedures clearly outlines proper escalation process when Control is performing outside of acceptable threshold',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M7',
      category: 'Monitoring Tools',
      question: 'Are additional Monitoring Tools needed to adequately assess Control performance?',
      guidance: 'Requirement that every key Control should have atleast one Monitoring tool. Consider if there are other complementary Monitoring tools needed to provide sufficient evidence that the key Control is operating effectively.',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M8',
      category: 'Monitoring Tools',
      question: 'Will modification of the existing, or selection of a new Monitoring tool, Strengthen oversight of the Control?',
      required: true,
      type: 'yes_no'
    },
    {
      id: 'M9',
      category: 'Monitoring Documentation',
      question: 'Link or Location of Instructions for performing the Monitoring',
      guidance: 'If link or location is provided, Version Control / Change Management history should be included in instructions',
      required: true,
      type: 'yes_no'
    }
  ]
};

// CMA Assessment Questions (WHO, WHEN, HOW, WHERE, WHAT framework)
// CMA uses text input fields for "Initial Review" and "Proposed changes" instead of Yes/No/N/A
export const CMA_ASSESSMENT_QUESTIONS = {
  control: [
    {
      id: 'CMA_C_WHO',
      category: 'WHO',
      question: 'Is the control performed by the appropriate person at the appropriate level?',
      guidance: 'Sufficient Authority? Possess proper competencies? Independent from the individual processing transactions? Appropriately resourced for Continuity?',
      initialReview: 'WHO - Is the Control performed by the appropriate person at the appropriate level?\nSalesforce entitlements and promotion systemic flows\nKnowledge Hub Procedures and Required promo transfer and extension adjustment\ntraining',
      required: true,
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_C_WHEN',
      category: 'WHEN',
      question: 'Is the control performed at an appropriate frequency and stage of the process?',
      guidance: 'Is there a need for a preventative control and not only detective monitoring? Is the control performed at the appropriate stage in the process? Is the control performed at the appropriate frequency?',
      initialReview: 'WHEN - Is the Control performed at an appropriate frequency and stage of the process?\nDaily - Manual-Preventative',
      required: true,
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_C_HOW',
      category: 'HOW',
      question: 'Is the control sustainably performed?',
      guidance: 'Are procedures in place for the control? What is the escalation process for fails and exceptions? Will the control still be performable should the operation and/or number of transactions grow? Is there adequate cover by individuals with the correct skills?',
      initialReview: 'HOW - Is the control sustainably performed?\nSystemically - Agent entitlement, verification flow, adjustment flow\nSystemically - escalation and stops built into the flow\nControl Procedures are published in the Knowledge Hub',
      required: true,
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_C_WHERE',
      category: 'WHERE',
      question: 'Is the control evidenced?',
      guidance: 'How and where is execution of the control documented and saved? Is the documentation sufficient and appropriate? Do both maker and checker document their performance of the control? Has record retention been considered?',
      initialReview: 'WHERE - Is the control evidenced?\nSalesforce Call Peg/Case Reporting\nProcedure in Knowledge Hub and Training in LMS',
      required: true,
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_C_WHAT',
      category: 'WHAT',
      question: 'Is it the right control to mitigate the risk?',
      guidance: 'Would the absence of this control expose Business to risk? Is there a better alternative? Is the control preventative vs detective? Does the control meet the control objective?',
      initialReview: 'WHAT - Is it the right control to mitigate the risk?\nNo, the correct control is not in place for agent handling. The manual control is detective in nature and the absence of an adequate control would put Business at risk. The control will need to be enhanced during the issue.',
      required: true,
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    }
  ],
  monitoring: [
    {
      id: 'CMA_M_WHO',
      category: 'WHO',
      question: 'Is the Monitoring performed by the appropriate person at the appropriate level?',
      initialReview: 'WHO - Is the Monitoring performed by the appropriate person at the appropriate level?\nControl Testing Unit CTU\nMonitoring is performed at appropriate level\nMonitoring is performed by Onshore/offshore team',
      required: true,
      guidance: '',
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_M_WHEN',
      category: 'WHEN',
      question: 'Is the Monitoring performed at an appropriate frequency and stage of the process?',
      initialReview: 'WHEN - Is the Monitoring performed at an appropriate frequency and stage of the process?\nManual- Detective Monitoring performs Weekly Basis/Reported Monthly',
      required: true,
      guidance: '',
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_M_HOW',
      category: 'HOW',
      question: 'Is the Monitoring sustainably performed?',
      initialReview: 'HOW - Is the Monitoring sustainably performed?\nMonitoring Procedures are housed, where? Remediation and Escalation of Fails are sent through Pega by selection of appropriate options. Results are reported by OCT on breaches greater than 5% monthly',
      required: true,
      guidance: '',
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_M_WHERE',
      category: 'WHERE',
      question: 'Is the Monitoring evidenced?',
      initialReview: 'WHERE - Is.the Monitoring evidenced?\nPega control system by uploading the file along with housing results of the review. Record retention requirements are outlined in the monitoring procedure.',
      required: true,
      guidance: '',
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    },
    {
      id: 'CMA_M_WHAT',
      category: 'WHAT',
      question: 'Is it right monitoring to mitigate the risk?',
      initialReview: 'WHAT - Is it right monitoring to mitigate the risk?\nThe objective of the monitoring is to ensure that promo transfers/extensions were processed based on policy and procedure. The monitoring is detective.\nEXPECTED RESULT - Monitoring will need to be evaluated based on the modified control. Monitoring expects to implement Digital Monitoring of all pending status request that are not completed within set threshold.',
      required: true,
      guidance: '',
      type: 'text' // Changed from 'yes_no' to 'text' for CMA
    }
  ]
};

// Assessment metadata
export const ASSESSMENT_METADATA = {
  CMDA: {
    name: 'Control & Monitoring Design Assessment',
    description: 'Comprehensive assessment of control and monitoring design adequacy',
    sections: [CMDA_CONTROL_QUESTIONS, CMDA_MONITORING_QUESTIONS],
    totalQuestions: CMDA_CONTROL_QUESTIONS.questions.length + CMDA_MONITORING_QUESTIONS.questions.length,
    requiredForAdequacy: {
      control: CMDA_CONTROL_QUESTIONS.minRequired,
      monitoring: CMDA_MONITORING_QUESTIONS.minRequired
    }
  },
  CMA: {
    name: 'Control & Monitoring Assessment',
    description: 'Assessment of existing control and monitoring for update/change',
    sections: [CMA_ASSESSMENT_QUESTIONS.control, CMA_ASSESSMENT_QUESTIONS.monitoring],
    totalQuestions: CMA_ASSESSMENT_QUESTIONS.control.length + CMA_ASSESSMENT_QUESTIONS.monitoring.length,
    requiredForAdequacy: {
      control: CMA_ASSESSMENT_QUESTIONS.control.length,
      monitoring: CMA_ASSESSMENT_QUESTIONS.monitoring.length
    }
  }
};
